import { CheckIcon, ClockIcon } from 'assets/icons';
import Badge from 'components/badge/badge';
import SeeMoreButton from 'components/seeMore/SeeMoreButton';
import Label from 'components/crm/ui/Label';
import { doneText, dueText, isLate } from 'helper/taskDates';
import { linkKey, taskLinks } from 'helper/taskLinks';
import { durationLabel } from 'helper/timeLabels';
import useText from 'hooks/useText';
import { useEffect, useState } from 'react';
import TaskContactCard from './TaskContactCard';
import TaskDescriptionField from './TaskDescriptionField';

// The linked records shown before « Voir plus ».
const SHOWN_LINKS = 3;

/**
 * The « Détail » tab of a task: under « Date & heure », its due day (red when
 * late) or the day it was done, every record it is linked to, its
 * description. A task has a single kind: no type badge, no e-mail to send. `openerOf(link)`: what opens a
 * linked record, or null. The records sit in one frame, a line between each,
 * the first three only until « Voir plus », the pill of the mission
 * description in jobbiz_app_v2, under the frame.
 */
const TaskDetailTab = ({ task, openerOf, onSave, canEdit = true }) => {
  const { text } = useText('tasks');
  const now = new Date();
  const late = isLate(task, now);
  const links = taskLinks(task);
  const [more, setMore] = useState(false);
  useEffect(() => setMore(false), [task.id]);
  const when = task.dueTime
    ? `${dueText(task, now, text)}, ${task.dueTime.slice(0, 5)} · ${durationLabel(task.durationMin ?? 15)}`
    : dueText(task, now, text);
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <Label>
          <span className="flex items-center gap-1.5">
            <ClockIcon wh={14} color="currentColor" />
            {text('dateTimeLabel')}
          </span>
        </Label>
        <div className="flex flex-wrap items-center gap-1.5">
          {task.doneAt ? (
            <Badge type="VALID">
              <CheckIcon w={12} h={9} color="currentColor" />
              {doneText(task, text)}
            </Badge>
          ) : (
            <Badge type={late ? 'ALERT' : 'PRIMARY'}>{when}</Badge>
          )}
        </div>
      </div>
      {links.length > 0 && (
        <div className="flex flex-col gap-2">
          <Label>{text('columnLinked')}</Label>
          <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200">
            {links.slice(0, more ? links.length : SHOWN_LINKS).map((link) => (
              <TaskContactCard key={linkKey(link)} link={link} task={task} onOpen={openerOf(link)} />
            ))}
          </div>
          {links.length > SHOWN_LINKS && (
            <SeeMoreButton open={more} onClick={() => setMore((current) => !current)}>
              {more ? text('seeLessLinks') : text('seeMoreLinks', { count: links.length - SHOWN_LINKS })}
            </SeeMoreButton>
          )}
        </div>
      )}
      <TaskDescriptionField
        key={task.id}
        initial={task.note ?? ''}
        onSave={(note) => onSave({ note })}
        canEdit={canEdit}
      />
    </div>
  );
};

export default TaskDetailTab;
